/**
 * @file add-group-categories.tsx
 * @deprecated Copied to app/modules/procurement/pages/master/category/add-group-categories.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/master/category/add-group-categories
 * @description Page component for adding group categories in the master category section.
 *   Allows users to create multiple group categories at once, each linked to a primary
 *   category and subcategory. Includes form validation and API integration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Missing auditLogMessage in categoryJSON.addGroupCategory API payload — see uprocure-admin
 *   add-group-categories.tsx (generateLogMessageForCategory + warehouseName).
 * @todo Unused import: generateLogMessageForCategory is imported but never used.
 */

import React, { FC, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import cross from "../../../../../_admin/assets/media/svg_uTrade/cross-red.svg";
import { Add, Categories, PAGE_LIMIT } from "../../../../../utils/constants";
import APICallService from "../../../../../api/apiCallService";
import { master } from "../../../../../api/apiEndPoints";
import { categoryJSON } from "../../../../../api/apiJSON/master";
import { masterToast } from "../../../../../utils/toast";
import { success } from "../../../../../Global/toast";
import clsx from "clsx";
import { CustomSelectWhite } from "../../../../custom/Select/CustomSelectWhite";
import Method from "../../../../../utils/methods";
import { useAuth } from "../../../auth";
import { warehouse as warehouseStoreString } from "../../../../../utils/storeString";
import { getKey } from "../../../../../Global/history";
import { useWarehouse } from "../../../../context/WarehouseContext";

const AddGroupCategories: FC = () => {
  const { selectedWarehouse } = useWarehouse();
  const depth = 2;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [primaryCategory, setPrimaryCategory] = useState([]);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<
    {
      groupCategoryName: boolean;
      primaryCategory: boolean;
      subCategory: boolean;
    }[]
  >([
    {
      groupCategoryName: false,
      primaryCategory: false,
      subCategory: false,
    },
  ]);
  const [subCategory, setSubCategory] = useState();
  const [selectedSubCategory, setSelectedSubCategory] = useState();
  const [groupCategory, setGroupCategory] = useState<
    {
      groupCategoryName: any;
      primaryCategory: string;
      subCategoryDefault: any;
      subCategory: string;
      subCategoryName: string;
    }[]
  >([
    {
      groupCategoryName: "",
      primaryCategory: "",
      subCategory: "",
      subCategoryDefault: null,
      subCategoryName: "",
    },
  ]);
  useEffect(() => {
    (async () => {
      if (!Method.hasPermission(Categories, Add, currentUser)) {
        return window.history.back();
      }
      setFetchLoader(true);
      await fetchPrimaryCategory(page, PAGE_LIMIT, depth);
      setFetchLoader(false);
    })();
  }, []);
  const fetchPrimaryCategory = async (
    pageNo: number,
    limit: number,
    categoriesDepth: number,
    search: string = "",
  ) => {
    setFetchLoader(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      searchTerm: search,
      needCount: true,
      categoriesDepth: categoriesDepth,
    };
    if (selectedWarehouse?.value) {
      params = {
        ...params,
        warehouse: selectedWarehouse?.value,
      };
    }

    let apiService = new APICallService(
      master.categoryList,
      params,
      "",
      "",
      "",
      "",
      Categories,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    // let data: any = [...primaryCategory];
    // if (pageNo === 1) {
    //   setTotalRecords(response.total);
    // } else {
    //   let prevTotal = totalRecords;
    //   setTotalRecords(prevTotal);
    // }
    if (response.records) {
      response.records = await response.records.map((val: any) => {
        if (val.categories) {
          return {
            value: val.title,
            name: val.title,
            label: (
              <>
                <span className="symbol symbol-xl-40px symbol-35px border border-r10px me-2">
                  <img src={val.image} className="p-1" />
                </span>
                <span className="fs-16 fw-600 text-black ">{val.title}</span>
              </>
            ),
            _id: val._id,
            subCategory: val.categories,
          };
        }
      });
      setTotalRecords(response.total);
      let data: any = (prevResults: any) =>
        pageNo === 1 ? response.records : [...prevResults, ...response.records];
      setSelectedSubCategory(data[0]?.subCategory[0]);
      setPrimaryCategory(data);
    }
    setFetchLoader(false);
  };
  const onMenuScrollToBottom = async () => {
    if (!(primaryCategory.length === totalRecords)) {
      let tempPage = page;
      tempPage = tempPage + 1;
      setPage(tempPage);
      await fetchPrimaryCategory(tempPage, PAGE_LIMIT, depth);
    }
  };
  const handleInputChange = async (newValue: string) => {
    setPage(1);
    setSearchTerm(newValue);
    await fetchPrimaryCategory(1, 0, depth, newValue);
  };
  const onMenuClose = async () => {
    setSearchTerm("");
  };
  const onMenuOpen = async () => {
    setPage(1);
    await fetchPrimaryCategory(1, PAGE_LIMIT, depth, searchTerm);
  };
  const handleAddMore = () => {
    if (!loading) {
      let temp = [...groupCategory];
      let validateTemp = [...validation];
      let length = temp.length - 1;
      if (
        !temp[length].groupCategoryName &&
        !temp[length].primaryCategory &&
        !temp[length].subCategory
      ) {
        validateTemp[length].groupCategoryName = true;
        validateTemp[length].primaryCategory = true;
        validateTemp[length].subCategory = true;
      } else if (!temp[length].groupCategoryName) {
        validateTemp[length].groupCategoryName = true;
      } else if (!temp[length].primaryCategory) {
        validateTemp[length].primaryCategory = true;
      } else if (!temp[length].subCategory) {
        validateTemp[length].subCategory = true;
      } else {
        validateTemp.push({
          groupCategoryName: false,
          primaryCategory: false,
          subCategory: false,
        });
        temp.push({
          groupCategoryName: "",
          primaryCategory: "",
          subCategory: "",
          subCategoryDefault: null,
          subCategoryName: "",
        });
        setGroupCategory(temp);
      }
      setValidation(validateTemp);
    }
  };
  const handleRemove = async (e: any, index: any) => {
    let temp = [...groupCategory];
    let validateTemp = [...validation];
    validateTemp.splice(index, 1);
    temp.splice(index, 1);
    setGroupCategory(temp);
    setValidation(validateTemp);
  };
  const handleCategoryName = (value: any, index: any) => {
    let temp = [...groupCategory];
    // temp[index].groupCategoryName = value
    // setGroupCategory(temp)
    let validateTemp = [...validation];
    temp[index].groupCategoryName = value.trimStart();
    if (value.trimStart().length > 0) {
      validateTemp[index].groupCategoryName = false;
    } else {
      validateTemp[index].groupCategoryName = true;
    }
    setGroupCategory(temp);
    setValidation(validateTemp);
  };
  const handlePrimaryCategoryChange = (event: any, index: any) => {
    let temp = [...groupCategory];
    let validateTemp = [...validation];
    temp[index].primaryCategory = event._id;
    validateTemp[index].primaryCategory = false;
    let subCategoryTemp = event.subCategory.map((val: any) => {
      return {
        value: val.title,
        name: val.title,
        label: (
          <>
            <span className="symbol symbol-xl-40px symbol-35px border border-r10px me-2">
              <img src={val.image} className="p-1" />
            </span>
            <span className="fs-16 fw-600 text-black ">{val.title}</span>
          </>
        ),
        _id: val._id,
        subCategory: val.categories,
      };
    });
    temp[index].subCategoryDefault = subCategoryTemp[0];
    temp[index].subCategory = subCategoryTemp[0]._id;
    setSubCategory(subCategoryTemp);
    setGroupCategory(temp);
  };
  const handleSubCategoryChange = (event: any, index: any) => {
    let temp = [...groupCategory];
    let validateTemp = [...validation];
    temp[index].subCategory = event._id;
    temp[index].subCategoryName = event.name;
    validateTemp[index].subCategory = false;
    temp[index].subCategoryDefault = event;
    setSelectedSubCategory(event);
    setGroupCategory(temp);
  };
  const handleSaveGroupCategory = async () => {
    let temp = [...groupCategory];
    let validateTemp = [...validation];
    let length = temp.length - 1;
    if (
      !temp[length].groupCategoryName &&
      !temp[length].primaryCategory &&
      !temp[length].subCategory
    ) {
      validateTemp[length].groupCategoryName = true;
      validateTemp[length].primaryCategory = true;
      validateTemp[length].subCategory = true;
    } else if (!temp[length].groupCategoryName) {
      validateTemp[length].groupCategoryName = true;
    } else if (!temp[length].primaryCategory) {
      validateTemp[length].primaryCategory = true;
    } else if (!temp[length].subCategory) {
      validateTemp[length].subCategory = true;
    } else {
      setLoading(true);
      let apiService = new APICallService(
        master.addGroupCategory,
        categoryJSON.addGroupCategory({ groupCategory: groupCategory }),
        "",
        "",
        "",
        "",
        Categories,
        selectedWarehouse?.value || null,
      );
      let response = await apiService.callAPI();
      if (response) {
        success(masterToast.addGroupCategory);
        navigate("/UProcure/master/categories");
      }
      setLoading(false);
    }
    setValidation(validateTemp);
  };
  return (
    <>
      <div className="p-5">
        <Row>
          <Col lg={12} className="mb-5">
            <h1 className="fs-22 fw-bolder">Add group category</h1>
          </Col>
          <Col>
            {groupCategory.map((val, index) => {
              return (
                <Col lg={12}>
                  <div className="border border-r10px bg-light position-relative mb-6 p-5">
                    <Row>
                      <Col lg={6} className="mb-6">
                        <Row className="align-items-center">
                          <Col lg={4}>
                            <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3">
                              Category name
                            </label>
                          </Col>
                          <Col lg={8}>
                            <input
                              type="text"
                              className={clsx(
                                "form-control form-control-solid bg-white h-60px border form-control-lg text-dark fs-16 fw-600 ",
                                validation[index].groupCategoryName
                                  ? "border-danger"
                                  : "",
                              )}
                              name="name"
                              placeholder="Type here…"
                              disabled={loading}
                              value={val.groupCategoryName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ): void =>
                                handleCategoryName(e.target.value, index)
                              }
                            />
                            <div className="fv-plugins-message-container">
                              <span className="text-danger fs-12 fw-bold">
                                {validation[index].groupCategoryName
                                  ? "Please enter group category name"
                                  : ""}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    {groupCategory.length == 1 ? (
                      <></>
                    ) : !loading ? (
                      <div className="group-categories-cross">
                        <img
                          src={cross}
                          height={16}
                          width={16}
                          onClick={(e) => {
                            handleRemove(e, index);
                          }}
                        />
                      </div>
                    ) : (
                      <></>
                    )}
                    <Row className="align-items-center">
                      <Col lg={6} className="mb-6">
                        <Row className="align-items-center">
                          <Col lg={4}>
                            <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3">
                              Primary category
                            </label>
                          </Col>
                          <Col lg={8}>
                            <CustomSelectWhite
                              border={
                                validation[index].primaryCategory
                                  ? "#e55451"
                                  : ""
                              }
                              disabled={loading}
                              loading={fetchLoader}
                              options={primaryCategory}
                              loadingMessage={"Fetching Data"}
                              isMulti={false}
                              onMenuScrollToBottom={onMenuScrollToBottom}
                              onMenuClose={() => {
                                onMenuClose();
                              }}
                              onMenuOpen={() => {
                                onMenuOpen();
                              }}
                              onInputChange={(
                                newValue: any,
                                { action }: any,
                              ) => {
                                if (action === "input-change") {
                                  handleInputChange(newValue);
                                }
                              }}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ): void => handlePrimaryCategoryChange(e, index)}
                            />
                            <div className="fv-plugins-message-container">
                              <span className="text-danger fs-12 fw-bold">
                                {validation[index].primaryCategory
                                  ? "Please select primary category"
                                  : ""}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      <Col lg={6} className="mb-6">
                        <Row className="align-items-center">
                          <Col lg={4}>
                            <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3">
                              Sub category
                            </label>
                          </Col>
                          <Col lg={8}>
                            <CustomSelectWhite
                              border={
                                validation[index].subCategory ? "#e55451" : ""
                              }
                              isLoading={fetchLoader}
                              options={subCategory}
                              default={groupCategory[index].subCategoryDefault}
                              value={groupCategory[index].subCategoryDefault}
                              loadingMessage={"Fetching Data"}
                              isMulti={false}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ): void => handleSubCategoryChange(e, index)}
                              disabled={loading}
                            />
                            <div className="fv-plugins-message-container">
                              <span className="text-danger fs-12 fw-bold">
                                {validation[index].subCategory
                                  ? "Please select sub category"
                                  : ""}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                </Col>
              );
            })}
          </Col>
        </Row>
        <div className="d-flex flex-column">
          <Link
            to="#"
            className="text-primary fs-16 fw-bolder"
            onClick={handleAddMore}
          >
            + Add new category
          </Link>
          <Row className="align-items-center mt-6 g-3 ">
            <Col xs="auto">
              <Button
                variant="danger"
                size="lg"
                className="uw-btn uw-btn-danger uw-btn-lg w-fit-content btn-active-danger"
                onClick={() => {
                  navigate("/UProcure/master/categories");
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              {" "}
              <Button
                variant="primary"
                size="lg"
                className="uw-btn uw-btn-primary uw-btn-lg w-fit-content"
                onClick={handleSaveGroupCategory}
                disabled={
                  loading ||
                  !validation.every(
                    (obj) =>
                      obj.groupCategoryName === false &&
                      obj.primaryCategory === false &&
                      obj.subCategory === false,
                  )
                }
              >
                {!loading && (
                  <span className="indicator-label fs-16 fw-bold">
                    Save category
                  </span>
                )}
                {loading && (
                  <span
                    className="indicator-progress fs-16 fw-bold"
                    style={{ display: "block" }}
                  >
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};
export default AddGroupCategories;
